import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "assets/images/logo.svg";
import footerLogo from "assets/images/footerLogo.svg";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  hasMenuPermission,
  hasPermission,
} from "components/constants/Permissions";
import { setIsPermissionChecked } from "store/reducers/authReducer";
import { ChevronDown, ChevronRight } from "lucide-react";
import { getNewWorkshopRequestCount } from "store/actions/workspaceSlice";
import {
  AmenityIcon,
  CityIcon,
  OverviewIcon,
  ReviewIcon,
  MyPlanIcon,
  OrderIcon,
  WorkspaceIcon,
  MeetingRoomIcon,
  WorkshopIcon,
  OfficeIcon,
  HotDeskIcon,
  BranchesIcon,
  SettingsIcon,
  WSManagementIcon,
  APILogsIcon,
  WebsiteRequestIcon,
  UsersIcon,
  CustomerIcon,
  CouponIcon,
  RewardIcons,
} from "components/common/SVGIcons";
import SidebarLink from "components/common/SidebarLink";
import SidebarMenu from "components/common/SidebarMenu";

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isSubMenuOpen, setIsSubMenuOpen] = useState({
    workshops: false,
    rewards: false,
  });

  const { isGettingUserDetails, adminPermissions, accountType, brandInfo } =
    useSelector((state) => state.auth);

  const { primaryColor } = brandInfo?.theme?.themeColors || {};

  const { newWorkshopRequestCount = null } = useSelector(
    (state) => state.Workspace || {}
  );

  // Function to handle toggle for sub-menu
  const toggleSubMenu = (menu) => {
    setIsSubMenuOpen((prevState) => ({
      ...prevState,
      [menu]: !prevState[menu],
    }));
  };

  // Function to add 'active' class to the active link
  const isActive = (paths) =>
    paths.includes(location.pathname) ? "active" : "";

  // ACTIVE or OPEN SUB LINKS
  useEffect(() => {
    const path = location.pathname;
    // Automatically set submenu state based on the current path
    setIsSubMenuOpen({
      workshops: path.includes("/bookings/workshops"),
      rewards: path.includes("/rewards"), // Changed to detect any rewards path
    });
  }, [location.pathname]);

  useEffect(() => {
    dispatch(setIsPermissionChecked(true)); // Start permission checking
    dispatch(getNewWorkshopRequestCount());
    const currentPath = location.pathname;

    // Map paths to permission IDs
    const permissionMap = {
      "/dashboards/overview": "1_1",
      "/dashboards/reviews": "1_2",
      "/dashboards/reviews/review-history": "1_3",
      "/dashboards/reviews/review-history": "1_4",
      "/bookings/bookings": "2_1",
      "/bookings/workspace": "2_2",
      "/bookings/meeting-rooms": "2_3",
      "/bookings/workshops": "2_5",
      "/bookings/offices": "2_6",
      "/bookings/hot-desks": "2_7",
      "/management/cities": "3_1",
      "/management/amenities": "3_2",
      "/management/branches": "3_3",
      "/plan/offers": "4_3",
      "/users": "5_2",
    };

    const currentPermissionId = permissionMap[currentPath];

    const checkPermissions = async () => {
      if (
        !isGettingUserDetails && // Ensure user details are loaded
        currentPermissionId &&
        !hasPermission(accountType, adminPermissions, currentPermissionId)
      ) {
        // Find the first accessible route
        const findFirstAccessibleRoute = () => {
          for (const group of adminPermissions) {
            for (const child of group.children) {
              if (child.permissions.isView) {
                const route = Object.keys(permissionMap).find(
                  (key) => permissionMap[key] === child.id
                );
                return route;
              }
            }
          }
          dispatch();
          return "/"; // Default redirect if no accessible routes
        };

        const redirectTo = findFirstAccessibleRoute();
        if (redirectTo) navigate(redirectTo, { replace: true });
      }

      // Simulate permission check delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      dispatch(setIsPermissionChecked(false)); // Stop checking after permission verification
    };

    checkPermissions();
  }, [
    location.pathname,
    accountType,
    adminPermissions,
    navigate,
    isGettingUserDetails,
    dispatch,
  ]); // Ensure dependencies are correct

  return (
    <div className="sidebar_menu">
      <div className="sidebar_logo company_logo">
        <Link to={"/dashboards/overview"}>
          <img
            src={
              brandInfo?.theme?.logo && brandInfo.theme.logo.trim() !== ""
                ? brandInfo.theme.logo
                : footerLogo
            }
            alt="Work Space Logo"
            width={150}
            height={96}
          />
        </Link>
      </div>
      <nav id="sidebar-menu">
        <ul>
          {hasMenuPermission(accountType, adminPermissions, 1) && (
            <li className="menu-title">{t("Dashboards")} </li>
          )}
          {hasPermission(accountType, adminPermissions, "1_1") && (
            <li>
              <Link
                to="/dashboards/overview"
                className={isActive(["/dashboards/overview"])}
              >
                <OverviewIcon color={primaryColor} />

                {t("Overview")}
              </Link>
            </li>
          )}
          {hasPermission(accountType, adminPermissions, "1_2") && (
            <li
              className={`menu-item`}
              onClick={() => toggleSubMenu("reviews")}
            >
              <Link
                to="/dashboards/reviews"
                className={`p_r ${isActive(["/dashboards/reviews"])}`}
              >
                <ChevronRight
                  className={`arrow_right_icon ${
                    isSubMenuOpen.reviews ? "active" : ""
                  }`}
                />
                <ReviewIcon color={primaryColor} />
                {t("Reviews")}
              </Link>
              <ul className={`sub-menu ${isSubMenuOpen.reviews ? "open" : ""}`}>
                {hasPermission(accountType, adminPermissions, "1_3") && (
                  <li>
                    <Link
                      to="/dashboards/reviews/review-history"
                      className={isActive([
                        "/dashboards/reviews/review-history",
                      ])}
                    >
                      {t("Reviews History")}
                    </Link>
                  </li>
                )}
              </ul>
            </li>
          )}
          {hasPermission(accountType, adminPermissions, "1_4") && (
            <li>
              <Link
                to="/membership/my-plan"
                className={isActive(["/membership/my-plan"])}
              >
                <MyPlanIcon color={primaryColor} />
                {t("My Plan")}
              </Link>
            </li>
          )}

          {/* BOOKINGS */}
          {hasMenuPermission(accountType, adminPermissions, 2) && (
            <li className="menu-title">{t("Bookings")}</li>
          )}

          {hasPermission(accountType, adminPermissions, "2_1") && (
            <li>
              <Link
                to="/bookings/bookings"
                className={isActive([
                  "/bookings/bookings",
                  "/bookings/bookings/booking-details",
                ])}
              >
                <OrderIcon color={primaryColor} />
                {t("Bookings")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "2_2") && (
            <li>
              <Link
                to="/bookings/workspace"
                className={isActive([
                  "/bookings/workspace",
                  "/bookings/workspace/add-new",
                  "/bookings/workspace/workspace-details",
                  "/bookings/workspace/edit-workspace",
                ])}
              >
                <WorkspaceIcon color={primaryColor} />
                {t("Workspace")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "2_3") && (
            <li>
              <Link
                to="/bookings/meeting-rooms"
                className={isActive([
                  "/bookings/meeting-rooms",
                  "/bookings/meeting-rooms/meeting-room-details",
                  "/bookings/meeting-rooms/update-meeting-room",
                  "/bookings/meeting-rooms/add-new",
                ])}
              >
                <MeetingRoomIcon color={primaryColor} />
                {t("Meeting Rooms")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "2_4") && (
            <li
              className={`menu-item`}
              onClick={() => toggleSubMenu("workshops")}
            >
              <Link
                to="/bookings/workshops"
                className={`p_r ${isActive([
                  "/bookings/workshops",
                  "/bookings/workshops/workshop-details",
                  "/bookings/workshops/update-workshop",
                ])}`}
              >
                <ChevronRight
                  className={`arrow_right_icon ${
                    isSubMenuOpen.workshops ? "active" : ""
                  }`}
                />
                {/* <img
                  src={arrowRight}
                  className={`arrow_right_icon ${
                    isSubMenuOpen.workshops ? "active" : ""
                  }`}
                  alt="Icon"
                  width={16}
                  height={16}
                /> */}
                <WorkshopIcon color={primaryColor} />
                {t("Workshops")}
                {newWorkshopRequestCount > 0 && (
                  <span className="notification-badge">
                    {newWorkshopRequestCount}
                  </span>
                )}
              </Link>
              <ul
                className={`sub-menu ${isSubMenuOpen.workshops ? "open" : ""}`}
              >
                <li>
                  <Link
                    to="/bookings/workshops/add-new"
                    className={isActive([
                      "/bookings/workshops/add-new" ||
                        "/bookings/workshops/update-workshop",
                    ])}
                  >
                    {t("Add New")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/bookings/workshops/workshops-requests"
                    className={isActive([
                      "/bookings/workshops/workshops-requests",
                    ])}
                  >
                    {t("Requests")}
                    {newWorkshopRequestCount > 0 && (
                      <span className="notification-badge-2">
                        {newWorkshopRequestCount}
                      </span>
                    )}
                  </Link>
                </li>
              </ul>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "2_5") && (
            <li>
              <Link
                to="/bookings/website-requests"
                className={isActive(["/bookings/website-requests"])}
              >
                <WebsiteRequestIcon color={primaryColor} />
                {t("Website Requests")}
              </Link>
            </li>
          )}
          {hasPermission(accountType, adminPermissions, "2_6") && (
            <li>
              <Link
                to="/bookings/offices"
                className={isActive([
                  "/bookings/offices",
                  "/bookings/offices/office-details",
                  "/bookings/offices/update-office",
                  "/bookings/offices/add-new",
                ])}
              >
                <OfficeIcon color={primaryColor} />
                {t("Offices")}
              </Link>
            </li>
          )}
          {hasPermission(accountType, adminPermissions, "2_7") && (
            <li>
              <Link
                to="/bookings/hot-desks"
                className={isActive([
                  "/bookings/hot-desks",
                  "/bookings/hot-desks/update-hot-desk",
                  "/bookings/hot-desks/add-new",
                  "/bookings/hot-desk/hot-desk-details",
                ])}
              >
                <HotDeskIcon color={primaryColor} />
                {t("Hot Desks")}
              </Link>
            </li>
          )}

          {hasMenuPermission(accountType, adminPermissions, 3) && (
            <li className="menu-title">{t("Management")}</li>
          )}
          <SidebarLink
            to="/management/settings"
            icon={CityIcon}
            label={t("Cities")}
            isActive={isActive(["/management/cities"])}
            disabled={!hasPermission(accountType, adminPermissions, "3_1")}
            // disabled={true}
            color={primaryColor}
          />

          {hasPermission(accountType, adminPermissions, "3_2") && (
            <li>
              <Link
                to="/management/amenities"
                className={isActive(["/management/amenities"])}
              >
                <AmenityIcon color={primaryColor} />
                {t("Amenities")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "3_3") && (
            <li>
              <Link
                to="/management/branches"
                className={isActive([
                  "/management/branches",
                  "/management/branches/add-branch",
                  "/management/branches/branch-details",
                  "/management/branches/update-branch",
                ])}
              >
                <BranchesIcon color={primaryColor} />
                {t("Branches")}
              </Link>
            </li>
          )}

          {hasPermission(accountType, adminPermissions, "3_4") && (
            <li>
              <Link
                to="/management/ws-management"
                className={isActive([
                  "/management/ws-management",
                  "/management/ws-management/add-new-closure",
                  "/management/ws-management/update-closure",
                  "/management/ws-management/closure-details",
                ])}
              >
                <WSManagementIcon color={primaryColor} />
                {t("WS Management")}
              </Link>
            </li>
          )}

          {/* Rewards Submenu */}
          <SidebarLink
            to="/management/settings"
            icon={SettingsIcon}
            label={t("Settings")}
            isActive={isActive([
              "/management/settings",
              "/management/settings/notifications",
              "/management/settings/privacy-and-security",
              "/management/settings/introduction-page",
              "/management/settings/global-settings",
            ])}
            disabled={!hasPermission(accountType, adminPermissions, "3_5")}
            // disabled={true}
            color={primaryColor}
          />
          <SidebarLink
            to="/management/api-logs"
            icon={APILogsIcon}
            label={t("API Logs")}
            isActive={isActive([
              "/management/api-logs",
              "/management/api-logs/details",
            ])}
            disabled={!hasPermission(accountType, adminPermissions, "4_2")}
            // disabled={true}
            color={primaryColor}
          />

          <SidebarMenu
            title={t("Rewards")}
            icon={RewardIcons}
            isActive={isActive([
              "/rewards/rewards",
              "/rewards/cashback",
              "/rewards/coupon",
              "/rewards/rewards-page",
            ])}
            isOpen={isSubMenuOpen.rewards}
            toggleSubMenu={() => toggleSubMenu("rewards")}
            subMenuItems={[
              {
                to: "/rewards/cashback",
                label: t("Cashback"),
                isActive: isActive(["/rewards/cashback"]),
              },
              {
                to: "/rewards/referral",
                label: t("Referral Code"),
                isActive: isActive(["/rewards/referral"]),
              },
            ]}
            disabled={!hasMenuPermission(accountType, adminPermissions, 4)}
            // disabled={true}
            color={primaryColor}
          />
          {hasPermission(accountType, adminPermissions, "4_3") && (
            <li>
              <Link
                to="/plan/offers"
                className={isActive([
                  "/plan/offers",
                  "/plan/offers/add-new-offer",
                  "/plan/offers/update-offer",
                ])}
              >
                <CouponIcon color={primaryColor} />

                {t("Plan Offers")}
              </Link>
            </li>
          )}
          <SidebarLink
            to="/rewards/coupons"
            icon={CouponIcon}
            label={t("Coupons")}
            isActive={isActive([
              "/rewards/coupons",
              "/rewards/coupons/add-new-coupon",
              "/rewards/coupons/update-coupon",
            ])}
            disabled={!hasPermission(accountType, adminPermissions, "4_2")}
            // disabled={true}
            color={primaryColor}
          />

          {/* CUSTOMERS & USERS */}
          {hasMenuPermission(accountType, adminPermissions, 5) && (
            <li className="menu-title">{t("Customers, Users")}</li>
          )}
          {hasPermission(accountType, adminPermissions, "5_1") && (
            <li>
              <Link
                className={isActive([
                  "/customers",
                  "/customers/customer-information",
                ])}
                to="/customers"
              >
                <CustomerIcon color={primaryColor} />
                {t("Customers")}
              </Link>
            </li>
          )}
          {accountType === "superadmin" && (
            <li>
              <Link className={isActive(["/roles"])} to="/roles">
                <UsersIcon color={primaryColor} />

                {t("Roles")}
              </Link>
            </li>
          )}

          {/* {hasPermission(accountType, adminPermissions, "5_2") && (
            <li>
              <Link
                className={isActive(["/users", "/users/user-details"])}
                to="/users"
              >
                <UsersIcon color={primaryColor} />
                {t("Users")}
              </Link>
            </li>
          )} */}
          <SidebarLink
            to="/users"
            icon={UsersIcon}
            label={t("Users")}
            isActive={isActive(["/users", "/users/user-details"])}
            disabled={!hasPermission(accountType, adminPermissions, "5_2")}
            // disabled={true}
            color={primaryColor}
          />
        </ul>
        <div className="footer_logo">
          {/* <img src={footerLogo} alt="logo" width={180} height={96} /> */}
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
